class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.openFromHeader(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.openFromHeader(cartLink);
      }
    });
  }

  async openFromHeader(triggeredBy) {
    if (this.headerOpenPending || this.classList.contains('active')) return;
    this.headerOpenPending = true;
    this.setActiveElement(triggeredBy);
    triggeredBy?.setAttribute('aria-busy', 'true');
    try {
      // History restores can retain old HTML even when the live Shopify cart has changed.
      // Read native sections before opening; this GET never adds or updates cart lines.
      const root = window.Shopify?.routes?.root || window.routes.cart_url.replace(/cart\/?$/, '');
      const sectionIds = this.getSectionsToRender().map((section) => section.id);
      const response = await fetch(`${root}?sections=${sectionIds.join(',')}&_hydros_cart=${Date.now()}`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Cart sections unavailable');
      const sections = await response.json();
      if (!sectionIds.every((id) => typeof sections[id] === 'string' && sections[id].trim())) {
        throw new Error('Cart sections incomplete');
      }
      const freshDrawer = this.getSectionDOM(sections['cart-drawer'], 'cart-drawer');
      if (!freshDrawer) throw new Error('Cart drawer unavailable');
      this.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      this.renderContents({ sections });
    } catch (error) {
      // A failed refresh must not present a cached empty or out-of-date drawer.
      window.location.assign(window.routes.cart_url);
    } finally {
      this.headerOpenPending = false;
      triggeredBy?.removeAttribute('aria-busy');
    }
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = requestAnimationFrame(() => {
      if (!this.classList.contains('active')) return;
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty') : this.querySelector('#CartDrawer');
      const focus = this.querySelector('.drawer__close');
      if (container && focus) trapFocus(container, focus);
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    cancelAnimationFrame(this.focusFrame);
    this.classList.remove('active');
    removeTrapFocus();
    document.body.classList.remove('overflow-hidden');
    const returnTarget = this.activeElement?.isConnected && !this.contains(this.activeElement)
      ? this.activeElement
      : document.getElementById('cart-icon-bubble');
    returnTarget?.focus({ preventScroll: true });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Browser history can restore a stale drawer after checkout without reloading Shopify's cart.
// Reload only BFCache restores; ordinary visits and the resulting reload do not repeat it.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) window.location.reload();
});
